import {
  Bottom,
  BottomTitle,
  Default,
  Div,
  Head,
  Heading,
  LeftDiv,
  SortableStatusWrapper,
  Status,
  StatusGroup
} from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { GroupByStatusInterface, StatusListInterface } from '../../../interfaces/ProjectInterface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BUTTON_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  PROJECT_SETTINGS_MENU_OPTION,
  STATUS_TYPE
} from '../../../global/constants';
import DeleteModal from '../../modals/deleteModal';
import { groupByStatus } from '../../../helpers/common';
import { deleteStatus, dispatchProjectError, updateStatusData } from '../../../services/projectServices';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  useDroppable
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Autotext, Box, IconHidden, LeftRow, Row, Subtitle, Title } from '../styles';
import Button from '../../button';
import { Dropdown, DropdownItem } from '../../dropdown';
import Modal from '../../modals/modal';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import IconButton from '../../button/iconButton';
import SwitchInput from '../../input/switchInput';

interface SortableStatusItemProps {
  item: StatusListInterface;
  type: number;
  renderStatusIcon: (type: number) => React.ReactNode;
  RenderContentItem: (item: StatusListInterface) => React.ReactNode;
  RenderMenuItems: (item: StatusListInterface) => React.ReactNode;
}

const SortableStatusItem: React.FC<SortableStatusItemProps> = ({
  item,
  type,
  renderStatusIcon,
  RenderContentItem,
  RenderMenuItems
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item['_id'],
    data: { type }
  });
  const cssTransform = CSS.Translate.toString(transform ? { ...transform } : null) || undefined;

  return (
    <SortableStatusWrapper
      ref={setNodeRef}
      isDragging={isDragging}
      cssTransform={cssTransform}
      cssTransition={transition || undefined}>
      <Bottom isDragging={isDragging}>
        <IconHidden
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...attributes}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...listeners}>
          <SVGIcon
            name='new-three-dots-vertical-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
            fill='var(--text-secondary)'
          />
        </IconHidden>
        <LeftDiv>
          {renderStatusIcon(type)}
          <BottomTitle>{item?.Name}</BottomTitle>
        </LeftDiv>
        <Dropdown
          modalTitle='Status'
          activeClassName='active-state'
          content={
            <Div>
              {RenderContentItem(item)}
              <IconButton
                iconName='three-dots-horizontal-icon'
                size={ICON_BUTTON_SIZE.SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                className='more-icon'
              />
            </Div>
          }
          trigger='click'>
          {RenderMenuItems(item)}
        </Dropdown>
      </Bottom>
    </SortableStatusWrapper>
  );
};

interface DroppableStatusGroupProps {
  type: number;
  children: React.ReactNode;
}

const DroppableStatusGroup: React.FC<DroppableStatusGroupProps> = ({ type, children }) => {
  const { setNodeRef } = useDroppable({ id: String(type) });
  return <StatusGroup ref={setNodeRef}>{children}</StatusGroup>;
};

interface Props {
  themeMode: { theme: string };
  loadingButton: boolean;
  setModalOpen: (value: boolean) => void;
  onUpdateStatus: (value: boolean) => void;
  setStatusType: (value: number) => void;
  isTemplateProject?: boolean;
  setStatusData: (value: StatusListInterface) => void;
}
const StatusSettingCard: React.FC<Props> = (props) => {
  const { setModalOpen, onUpdateStatus, setStatusType, isTemplateProject, loadingButton, setStatusData } = props;
  //States
  const [deleteModelOpen, setDeleteModalOpen] = useState(false);
  const [statusList, setStatusList] = useState<GroupByStatusInterface[]>([]);
  const [deleteItem, setDeleteItem] = useState<StatusListInterface>();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeItem, setActiveItem] = useState<StatusListInterface | null>(null);
  const [activeItemType, setActiveItemType] = useState<number | null>(null);
  const [sourceContainerId, setSourceContainerId] = useState<number | null>(null);

  //Mobx store variables
  const { projectStore, projectTemplateStore } = rootStore;
  const { currentProject: projectDetails } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const currentProject = isTemplateProject ? currentProjectTemplate : projectDetails;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const statusGroups = useMemo(
    () => [
      { type: STATUS_TYPE.BACKLOG, label: 'Backlog' },
      { type: STATUS_TYPE.UNSTARTED, label: 'Unstarted' },
      { type: STATUS_TYPE.IN_PROGRESS, label: 'In Progress' },
      { type: STATUS_TYPE.IN_REVIEW, label: 'In Review' },
      { type: STATUS_TYPE.COMPLETED, label: 'Completed' },
      { type: STATUS_TYPE.CANCELLED, label: 'Cancelled' }
    ],
    []
  );

  const loadData = useCallback(async () => {
    const groupedStatusData = groupByStatus(currentProject?.statusData || [], 'Type');
    const newStatusList = statusGroups.map((group) => {
      const existingGroup = groupedStatusData.find((g) => g.Type === group.type);
      return {
        Type: group.type,
        data: existingGroup ? existingGroup.data : []
      };
    });
    setStatusList(newStatusList);
  }, [currentProject?.statusData, statusGroups]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getGroupIds = useCallback(
    (type: number) => {
      const statusData = statusList.find((item) => item?.Type === type);
      return statusData?.data.map((item) => item['_id']) || [];
    },
    [statusList]
  );

  const findContainerType = useCallback(
    (id: string): number | undefined => {
      const asNumber = Number(id);
      if (statusList.find((group) => group.Type === asNumber)) {
        return asNumber;
      }
      for (const group of statusList) {
        if (group.data.some((item) => item['_id'] === id)) {
          return group.Type;
        }
      }
      return undefined;
    },
    [statusList]
  );

  const OpenDeleteModel = useCallback((item: StatusListInterface) => {
    setDeleteModalOpen(true);
    setDeleteItem(item);
  }, []);

  const onClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  const onClickAddGroup = useCallback(
    (type: number) => {
      setModalOpen(true);
      setStatusType(type);
    },
    [setModalOpen, setStatusType]
  );

  const onClickEdit = useCallback(
    (item: StatusListInterface) => {
      try {
        setStatusData(item);
        setModalOpen(true);
      } catch (error) {
        console.log('error', error);
      }
    },
    [setModalOpen, setStatusData]
  );

  const handleDelete = useCallback(async () => {
    setDeleteLoading(true);
    try {
      if (isTemplateProject) {
        const updatedStatusList = currentProject?.statusData?.filter(
          (status) => status?.['_id'] !== deleteItem?.['_id']
        );
        const isStatusInUse = currentProject?.tasks?.some((task) => task?.statusId === deleteItem?.['_id']);
        if (isStatusInUse) {
          dispatchProjectError(`Unable to delete status "${deleteItem?.Name}", as it is currently in use!`);
        } else {
          rootStore.projectTemplateStore.currentProjectTemplate?.setStatusData(updatedStatusList || []);
        }
      } else {
        const statusUseTasks = currentProject?.tasks?.filter((task) => task?.statusId === deleteItem?.['_id']);
        if (!statusUseTasks) return;
        if (statusUseTasks.length > 0) {
          dispatchProjectError(`Unable to delete status "${deleteItem?.Name}", as it is currently in use!`);
        } else {
          const updatedStatusList = currentProject?.statusData?.filter(
            (status) => status?.['_id'] !== deleteItem?.['_id']
          );
          rootStore.projectStore.currentProject?.setStatusData(updatedStatusList || []);
          await deleteStatus(currentProject?.id, deleteItem?.['_id']);
        }
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setDeleteModalOpen(false);
      setDeleteLoading(false);
    }
  }, [isTemplateProject, currentProject?.statusData, currentProject?.tasks, currentProject?.id, deleteItem]);

  const renderStatusIcon = useCallback((type: number) => {
    switch (type) {
      case STATUS_TYPE.BACKLOG:
        return (
          <SVGIcon
            name='task-status-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.UNSTARTED:
        return (
          <SVGIcon
            name='task-todo-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.IN_PROGRESS:
        return (
          <SVGIcon
            name='task-inprogress-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.IN_REVIEW:
        return (
          <SVGIcon
            name='task-inreview-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      case STATUS_TYPE.COMPLETED:
        return (
          <SVGIcon
            name='complete-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
            className='complete-icon'
          />
        );
      case STATUS_TYPE.CANCELLED:
        return (
          <SVGIcon
            name='task-cancelled-icon'
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
        );
      default:
        return <></>;
    }
  }, []);

  const onClickDefaultSet = useCallback(
    async (statusDetails: StatusListInterface) => {
      if (isTemplateProject) {
        const updatedStatus = currentProject?.statusData?.map((x) => {
          return { ...x, Default: x['_id'] === statusDetails['_id'] };
        });
        rootStore.projectTemplateStore.currentProjectTemplate?.setStatusData(updatedStatus || []);
      } else {
        try {
          const result = await updateStatusData(currentProject?.id, statusDetails['_id'], { default: true });
          if (result) {
            const updatedStatus = currentProject?.statusData?.map((x) => {
              return { ...x, Default: x['_id'] === statusDetails['_id'] };
            });
            rootStore.projectStore.currentProject?.setStatusData(updatedStatus || []);
          }
        } catch (error) {
          console.log('error', error);
        }
      }
    },
    [isTemplateProject, currentProject]
  );

  const handleMenuClick = useCallback(
    (key: number | string, item: StatusListInterface) => {
      if (key === PROJECT_SETTINGS_MENU_OPTION.EDIT) {
        onClickEdit(item);
      } else if (key === PROJECT_SETTINGS_MENU_OPTION.SET_AS_DEFAULT) {
        onClickDefaultSet(item);
      } else {
        OpenDeleteModel(item);
      }
    },
    [OpenDeleteModel, onClickDefaultSet, onClickEdit]
  );
  const RenderMenuItems = useCallback(
    (item: StatusListInterface) => {
      const MenuItems = [
        { label: 'Edit', key: PROJECT_SETTINGS_MENU_OPTION.EDIT },
        { label: 'Set as Default', key: PROJECT_SETTINGS_MENU_OPTION.SET_AS_DEFAULT },
        { label: 'Delete', key: PROJECT_SETTINGS_MENU_OPTION.DELETE, tone: 'critical' }
      ];

      return MenuItems.map((menuItem) => (
        <DropdownItem
          label={menuItem.label}
          key={menuItem?.key}
          tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
          onClick={() => handleMenuClick(menuItem.key, item)}
        />
      ));
    },
    [handleMenuClick]
  );

  const RenderContentItem = useCallback(
    (item: StatusListInterface) => {
      return item?.['_id'] === currentProject?.defaultStatus?.['_id'] ? <Default>Default</Default> : null;
    },
    [currentProject?.defaultStatus]
  );

  const getStatusGroupData = useCallback(
    (type: number) => {
      const statusData = statusList.find((item) => item?.Type === type);
      return statusData?.data || [];
    },
    [statusList]
  );

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const id = active.id as string;
      const type = findContainerType(id);
      if (type !== undefined) {
        const group = statusList.find((item) => item?.Type === type);
        const item = group?.data.find((d) => d['_id'] === id);
        setActiveItem(item || null);
        setActiveItemType(type);
        setSourceContainerId(type);
      }
    },
    [findContainerType, statusList]
  );

  const onDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      const activeContainer = findContainerType(activeId);
      const overContainer = findContainerType(overId);

      if (activeContainer === undefined || overContainer === undefined || activeContainer === overContainer) return;

      setStatusList((prev) => {
        const activeGroup = prev.find((g) => g.Type === activeContainer);
        const overGroup = prev.find((g) => g.Type === overContainer);
        if (!activeGroup || !overGroup) return prev;

        const activeItems = [...activeGroup.data];
        const overItems = [...overGroup.data];

        const activeIdx = activeItems.findIndex((item) => item['_id'] === activeId);
        if (activeIdx === -1) return prev;

        const [movedItem] = activeItems.splice(activeIdx, 1);

        const isOverContainer = overId === String(overContainer);
        const overIndex = overItems.findIndex((item) => item['_id'] === overId);
        const insertIndex = isOverContainer ? overItems.length : overIndex >= 0 ? overIndex : overItems.length;

        overItems.splice(insertIndex, 0, movedItem);

        return prev.map((g) => {
          if (g.Type === activeContainer) return { ...g, data: activeItems };
          if (g.Type === overContainer) return { ...g, data: overItems };
          return g;
        });
      });
    },
    [findContainerType]
  );

  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveItem(null);
      setActiveItemType(null);

      if (!over) {
        setSourceContainerId(null);
        return;
      }

      const activeId = active.id as string;
      const overId = over.id as string;
      const overContainer = findContainerType(overId);
      if (overContainer === undefined) {
        setSourceContainerId(null);
        return;
      }

      const sourceType = sourceContainerId ?? overContainer;
      const destinationType = overContainer;
      const isSameContainer = sourceType === destinationType;

      setSourceContainerId(null);

      // Use original data from currentProject for index calculation (not mutated statusList)
      const originalGrouped = groupByStatus(currentProject?.statusData, 'Type');
      const originalDestGroup = originalGrouped.find((g: GroupByStatusInterface) => g.Type === destinationType);
      const originalDestItems: StatusListInterface[] = originalDestGroup?.data || [];
      // Filter out the dragged item to get clean destination list
      const destItems = originalDestItems.filter((item) => item['_id'] !== activeId);

      let updatedTaskIndex: number;

      if (isSameContainer) {
        const overIdx = originalDestItems.findIndex((item) => item['_id'] === overId);
        const activeIdx = originalDestItems.findIndex((item) => item['_id'] === activeId);
        if (activeIdx === overIdx) return;

        // Immediately update statusList to prevent visual jerk
        setStatusList((prev) =>
          prev.map((g) => {
            if (g.Type !== destinationType) return g;
            return { ...g, data: arrayMove(g.data, activeIdx, overIdx) };
          })
        );

        if (overIdx === 0) {
          updatedTaskIndex = originalDestItems[0]?.Index ? originalDestItems[0].Index - 100 : new Date().getTime();
        } else if (overIdx === originalDestItems.length - 1) {
          const lastItem = originalDestItems[originalDestItems.length - 1];
          updatedTaskIndex = lastItem?.Index ? lastItem.Index + 100 : new Date().getTime();
        } else if (overIdx > activeIdx) {
          const prevIndex = originalDestItems[overIdx]?.Index || 0;
          const nextIndex = originalDestItems[overIdx + 1]?.Index || prevIndex + 200;
          updatedTaskIndex = (prevIndex + nextIndex) / 2;
        } else {
          const prevIndex = originalDestItems[overIdx - 1]?.Index || 0;
          const nextIndex = originalDestItems[overIdx]?.Index || prevIndex + 200;
          updatedTaskIndex = (prevIndex + nextIndex) / 2;
        }
      } else {
        // Cross-group: calculate index from clean destination list (without dragged item)
        const finalIndex = over.data.current?.sortable?.index ?? destItems.length;
        if (destItems.length === 0) {
          updatedTaskIndex = new Date().getTime();
        } else if (finalIndex === 0) {
          updatedTaskIndex = destItems[0]?.Index ? destItems[0].Index - 100 : new Date().getTime();
        } else if (finalIndex >= destItems.length) {
          const lastItem = destItems[destItems.length - 1];
          updatedTaskIndex = lastItem?.Index ? lastItem.Index + 100 : new Date().getTime();
        } else {
          const prevIndex = destItems[finalIndex - 1]?.Index || 0;
          const nextIndex = destItems[finalIndex]?.Index || prevIndex + 200;
          updatedTaskIndex = (prevIndex + nextIndex) / 2;
        }
      }

      const updatedStatus: StatusListInterface[] =
        currentProject?.statusData?.map((x) => {
          if (x['_id'] === activeId) {
            return { ...x, Index: updatedTaskIndex, Type: destinationType };
          } else return x;
        }) || [];

      if (!isTemplateProject) {
        rootStore.projectStore.currentProject?.setStatusData(updatedStatus);
        const payload: { index: number; type?: number } = { index: updatedTaskIndex };
        if (!isSameContainer) {
          payload.type = destinationType;
        }
        await updateStatusData(currentProject?.id, activeId, payload);
      } else {
        rootStore.projectTemplateStore.currentProjectTemplate?.setStatusData(updatedStatus);
      }
    },
    [currentProject?.statusData, currentProject?.id, isTemplateProject, sourceContainerId, findContainerType]
  );

  return (
    <>
      <Box>
        <Row>
          <LeftRow>
            <Title>Statuses</Title>
            <Subtitle>Statuses define task progression and can be customized and reordered</Subtitle>
          </LeftRow>
          <Autotext>
            {loadingButton ? (
              <Button variant={BUTTON_TYPE.PRIMARY} isLoading={loadingButton} />
            ) : (
              <SwitchInput
                checked={currentProject?.statusEnable || false}
                onChange={() => onUpdateStatus(!currentProject?.statusEnable)}
              />
            )}
          </Autotext>
        </Row>
        {currentProject?.statusEnable && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragCancel={() => {
              setActiveItem(null);
              setActiveItemType(null);
              setSourceContainerId(null);
            }}
            onDragEnd={onDragEnd}>
            <Status>
              {statusGroups.map(({ type, label }) => (
                <SortableContext
                  key={type}
                  id={String(type)}
                  items={getGroupIds(type)}
                  strategy={verticalListSortingStrategy}>
                  <DroppableStatusGroup type={type}>
                    <Head>
                      <Heading>{label}</Heading>
                      <IconButton
                        iconName='plus-icon'
                        size={ICON_BUTTON_SIZE.SMALL}
                        variant={ICON_BUTTON_TYPE.TERTIARY}
                        onClick={() => onClickAddGroup(type)}
                      />
                    </Head>
                    {getStatusGroupData(type).map((item) => (
                      <SortableStatusItem
                        key={item['_id']}
                        item={item}
                        type={type}
                        renderStatusIcon={renderStatusIcon}
                        RenderContentItem={RenderContentItem}
                        RenderMenuItems={RenderMenuItems}
                      />
                    ))}
                  </DroppableStatusGroup>
                </SortableContext>
              ))}
            </Status>
            <DragOverlay>
              {activeItem && activeItemType !== null ? (
                <Bottom isDragging>
                  <IconHidden>
                    <SVGIcon
                      name='new-three-dots-vertical-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='var(--text-secondary)'
                    />
                  </IconHidden>
                  <LeftDiv>
                    {renderStatusIcon(activeItemType)}
                    <BottomTitle>{activeItem?.Name}</BottomTitle>
                  </LeftDiv>
                  <Div>
                    {RenderContentItem(activeItem)}
                    <IconButton
                      iconName='three-dots-horizontal-icon'
                      size={ICON_BUTTON_SIZE.SMALL}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                      className='more-icon'
                    />
                  </Div>
                </Bottom>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Box>
      <Modal open={deleteModelOpen} onClose={onClose} maxWidth={'334px'}>
        <DeleteModal
          description='Are you sure you want to delete this status?'
          modalTitle='Delete Status?'
          onOk={handleDelete}
          onClose={onClose}
          loading={deleteLoading}
        />
      </Modal>
    </>
  );
};
export default observer(StatusSettingCard);
